import { useState } from "react";
import { api, isError } from "../api";
import { NewsPanel } from "../components/NewsPanel";
import { useNewsPoll } from "../hooks/useNewsPoll";

export function NewsPage() {
  const { events, asOf } = useNewsPoll(24);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; tone: "ok" | "warn" | "error" } | null>(
    null,
  );

  // The scheduler pulls every `news.refresh_interval_min` minutes and only
  // starts one interval after boot, so right after a deploy the calendar is
  // empty for a while. This triggers the same refresh on demand and shows
  // the count, which is also the quickest way to tell "feeds are off" from
  // "feeds returned nothing".
  async function refreshNow() {
    setBusy(true);
    setMessage(null);
    const r = await api.newsRefresh();
    setBusy(false);
    if (isError(r)) {
      setMessage({ text: `Refresh failed: ${r.error}`, tone: "error" });
      return;
    }
    if (r.written === 0) {
      setMessage({
        text:
          "Refresh ran but wrote 0 events. Check Settings → News scheduler + Forex Factory " +
          "are on, and that the bridge is connected (MQL5 feed).",
        tone: "warn",
      });
    } else {
      setMessage({ text: `Refreshed — ${r.written} events written.`, tone: "ok" });
    }
  }

  // Historical calendar for backtests (G5): the live feeds only keep a
  // few days, so a backtest's news gate needs history imported.
  async function importCalendar(file: File) {
    setBusy(true);
    setMessage(null);
    const r = await api.newsImport(file);
    setBusy(false);
    if (isError(r)) {
      setMessage({ text: `Import failed: ${r.error}`, tone: "error" });
      return;
    }
    const first = r.problems[0] ? ` First problem: ${r.problems[0]}` : "";
    setMessage({
      text:
        `Imported ${r.inserted} events (${r.already_present} already stored, ` +
        `${r.rejected} rejected).${first}`,
      tone: r.rejected > 0 ? "warn" : "ok",
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-bold tracking-tight">Economic calendar</h1>
        <button
          type="button"
          onClick={() => void refreshNow()}
          disabled={busy}
          className="rounded-md border border-border px-3 py-1.5 text-xs uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
        >
          {busy ? "refreshing…" : "refresh now"}
        </button>
      </header>
      <label
        className="flex flex-wrap items-center gap-2 text-xs text-fg-muted"
        title="CSV columns: time (ISO-8601 UTC), currency, impact (high/medium/low), event; optional actual, forecast, previous. Past events only."
      >
        Import a historical calendar CSV for backtests:
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importCalendar(f);
            e.target.value = "";
          }}
        />
      </label>
      {message && (
        <p
          className={`rounded-md px-3 py-2 text-sm ${
            message.tone === "ok"
              ? "border border-success/40 bg-success/10 text-success"
              : message.tone === "warn"
                ? "border border-warn/40 bg-warn/10 text-warn"
                : "border border-danger/40 bg-danger/10 text-danger"
          }`}
        >
          {message.text}
        </p>
      )}
      <NewsPanel events={events} asOf={asOf} />
    </div>
  );
}
